import AgentBreadcrumb from '@/components/Agents/AgentBreadcrumb/AgentBreadcrumb';
import { agentDisplayMap, parseAgentUrlSlug } from '@/components/Agents/AgentUtils';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionApiId } from '@/model/Agent.model';
import { type FC } from 'react';
import { useParams } from 'react-router-dom';
import ActivityPanel from './ActivityPanel/ActivityPanel';
import AgentSessionTree from './AgentSessionTree';
import SessionInputsPanel from './SessionInputsPanel/SessionInputsPanel';

/**
 * Page for an agent session: a three-column body. The left (`SessionInputsPanel`) and right
 * (`ActivityPanel`) columns are fixed-width, independently scrollable DOM; the center flexes to fill
 * and is the only pan/zoom canvas (`AgentSessionTree`, which owns React Flow).
 *
 * Reached at `/ai-agents/:slug/sessions/:sessionApiId`. Snapshot-only for now — the tree is hydrated
 * from `GET /tree`; no live streaming yet.
 */
const AgentSessionPage: FC = () => {
  const { slug, sessionApiId } = useParams<{ slug: string; sessionApiId: string }>();
  const agentType = _notNil(slug) ? parseAgentUrlSlug(slug) : undefined;
  const agentName = _notNil(agentType) ? agentDisplayMap[agentType].name : 'Session';

  if (_isNil(sessionApiId)) {
    return (
      <div className="ph4 pb4 pt3" data-test-component="AgentSessionPage">
        <p className="mid-gray">Agent session not found.</p>
      </div>
    );
  }

  // `minHeight: 0` lets this flex-column shrink below content size so the body scrolls/fits instead of
  // overflowing the viewport; there is no `min-h-0` Tachyons utility. (The inner row uses `flex-auto`,
  // which already sets min-width/min-height: 0.)
  return (
    <div
      className="flex flex-column h-100 bg-light-gray"
      style={{ minHeight: 0 }}
      data-test-component="AgentSessionPage"
    >
      <div className="ph4 pt3">
        <AgentBreadcrumb current={agentName} />
      </div>
      <div className="flex flex-auto">
        <SessionInputsPanel />
        <div className="flex-auto h-100">
          {/* key forces a remount (fresh fetch + engine) when the route switches sessions. */}
          <AgentSessionTree key={sessionApiId} sessionApiId={sessionApiId as AgentSessionApiId} />
        </div>
        <ActivityPanel />
      </div>
    </div>
  );
};

export default AgentSessionPage;
